import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from "react";
import type {
  default as HlsType,
  ErrorData,
  Level,
  LoaderCallbacks,
  LoaderConfiguration,
  LoaderContext,
} from "hls.js";
import type { VideoChapter, VideoPlayback } from "@lp/shared";
import { Captions, CaptionsOff, Check, Gauge, Loader2, Maximize, Pause, Play, SlidersHorizontal, Volume2, VolumeX } from 'lucide-react';
import { Alert, Badge, Button, Card, clsx } from "../ui.js";
import { formatDuration } from "../../lib/format.js";
import { useMediaToken, withMediaToken } from "../../lib/useMediaToken.js";
import { useProgressHeartbeat } from "../../lib/useProgressHeartbeat.js";

const SPEEDS = [0.75, 1, 1.25, 1.5, 2] as const;
/** Below this, resuming silently would be more annoying than helpful. */
const RESUME_PROMPT_THRESHOLD_SEC = 30;

export type VideoPlayerHandle = {
  /** Used by chat citations to jump into the lecture. */
  seekTo: (sec: number) => void;
  /** Used when the player is tabbed out of view, so it does not play unseen. */
  pause: () => void;
};

type Engine = "hls" | "native" | "unsupported" | "pending";

export function VideoPlayer({
  video,
  sessionId,
  title,
  resumeFromSec,
  resumeIsDeepLink = false,
  completed,
  onCompleted,
  ref,
}: {
  video: VideoPlayback;
  sessionId: string;
  title: string;
  /** Where the learner stopped last time, or a `?t=` deep link. */
  resumeFromSec: number;
  /** True when the position came from an explicit link, so jumping is the intent. */
  resumeIsDeepLink?: boolean;
  completed: boolean;
  onCompleted: () => void;
  ref?: Ref<VideoPlayerHandle>;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const hlsRef = useRef<HlsType | null>(null);
  const seededRef = useRef(false);

  /**
   * The source is pinned to the first playable URL this player is handed.
   *
   * Every refetch of the session mints a *new* media token, so following
   * `video.hlsUrl` would tear hls.js down and restart playback every time
   * anything on the page invalidated the session query — including the
   * completion beat. Token freshness is `useMediaToken`'s job; the source is
   * not where it belongs.
   */
  const [src, setSrc] = useState<string | null>(video.hlsUrl);
  useEffect(() => {
    if (video.hlsUrl && !src) setSrc(video.hlsUrl);
  }, [video.hlsUrl, src]);

  const {
    tokenRef,
    rotation,
    error: tokenError,
  } = useMediaToken(src, video.expiresInSec);

  const [engine, setEngine] = useState<Engine>("pending");
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [levels, setLevels] = useState<Level[]>([]);
  const [activeLevel, setActiveLevel] = useState(-1);
  const [levelChoice, setLevelChoice] = useState(-1);

  const [playing, setPlaying] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [muted, setMuted] = useState(false);
  const [rate, setRate] = useState(1);
  const [captionsOn, setCaptionsOn] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(video.durationSec ?? 0);
  const [scrubbing, setScrubbing] = useState(false);

  // A deep link is an explicit instruction to jump; a remembered position past
  // half a minute is a question worth asking before moving the playhead.
  const [resumeOffer, setResumeOffer] = useState(
    !resumeIsDeepLink &&
      resumeFromSec > RESUME_PROMPT_THRESHOLD_SEC &&
      !completed,
  );

  const ready = video.status === "ready" && !!src;

  useProgressHeartbeat({
    sessionId,
    videoRef,
    enabled: ready,
    durationSec: video.durationSec,
    onJustCompleted: onCompleted,
  });

  // ------------------------------------------------------------ engine setup ---

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !src) return;

    setFatalError(null);

    // hls.js is ~180 kB gzipped — a fifth of the whole bundle. Importing it on
    // demand lets the session page paint (notes, quiz, chat) while the engine
    // is still arriving, instead of blocking first render on a library that
    // only the video needs. `cancelled` covers the learner navigating away, or
    // the source changing, before the module resolves.
    let cancelled = false;
    let teardown: (() => void) | null = null;

    void (async () => {
      const el2 = el;
      const { default: Hls } = await import("hls.js");
      if (cancelled) return;

      if (Hls.isSupported()) {
        /**
         * Every request hls.js makes is rewritten to carry the token held *now*.
         * That is the whole token-rotation story on this path: when
         * `useMediaToken` swaps in a fresh token, the next playlist or segment
         * simply goes out with it. Nothing is reloaded and playback never pauses.
         */
        const BaseLoader = Hls.DefaultConfig.loader;
        class TokenLoader extends BaseLoader {
          override load(
            context: LoaderContext,
            config: LoaderConfiguration,
            callbacks: LoaderCallbacks<LoaderContext>,
          ): void {
            const token = tokenRef.current;
            if (token) context.url = withMediaToken(context.url, token);
            super.load(context, config, callbacks);
          }
        }

        const hls = new Hls({
          loader: TokenLoader,
          enableWorker: true,
          lowLatencyMode: false,
          backBufferLength: 60,
        });
        hlsRef.current = hls;
        setEngine("hls");

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setLevels(hls.levels.slice());
        });
        hls.on(Hls.Events.LEVEL_SWITCHED, (_evt, data) => {
          setActiveLevel(data.level);
        });
        hls.on(Hls.Events.ERROR, (_evt, data: ErrorData) => {
          if (!data.fatal) return;
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            hls.startLoad();
            return;
          }
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
            return;
          }
          setFatalError(
            "Playback stopped and could not be restarted. Reloading the page usually fixes it — your progress is saved.",
          );
          hls.destroy();
        });

        hls.attachMedia(el2);
        hls.loadSource(src);

        teardown = () => {
          hls.destroy();
          hlsRef.current = null;
        };
        return;
      }

      if (el2.canPlayType("application/vnd.apple.mpegurl")) {
        // Safari plays HLS itself. It also owns the segment requests, so a
        // rotated token can only reach it through the source URL — see below.
        setEngine("native");
        el2.src = src;
        return;
      }

      setEngine("unsupported");
    })();

    return () => {
      cancelled = true;
      teardown?.();
    };
  }, [src, tokenRef]);

  /**
   * Seed the playhead once the media knows how long it is. A position the
   * learner was not asked about (a deep link, or a few seconds in) is applied
   * silently; anything the resume card is offering waits for them to choose.
   */
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !ready || seededRef.current) return;
    if (resumeOffer || resumeFromSec <= 0) return;

    const apply = (): void => {
      if (seededRef.current) return;
      seededRef.current = true;
      const limit =
        Number.isFinite(el.duration) && el.duration > 0
          ? el.duration - 1
          : undefined;
      try {
        el.currentTime = limit ? Math.min(resumeFromSec, limit) : resumeFromSec;
      } catch {
        /* the media refused the seek; playback simply starts at the beginning */
      }
    };

    if (el.readyState >= 1) apply();
    else el.addEventListener("loadedmetadata", apply, { once: true });

    return () => el.removeEventListener("loadedmetadata", apply);
  }, [ready, resumeOffer, resumeFromSec]);

  /**
   * Native HLS cannot be handed a token mid-flight, so on Safari a rotation
   * re-points the source and restores the playhead. It is a re-buffer rather
   * than a seamless swap, which is why it happens at 75% of the token's life
   * and not at the last second.
   */
  useEffect(() => {
    if (engine !== "native" || rotation === 0) return;
    const el = videoRef.current;
    if (!el || !src) return;

    const at = el.currentTime;
    const wasPlaying = !el.paused;
    const restore = (): void => {
      el.removeEventListener("loadedmetadata", restore);
      try {
        el.currentTime = at;
      } catch {
        /* the browser refused the seek; playback still continues from 0 */
      }
      if (wasPlaying) void el.play().catch(() => undefined);
    };
    el.addEventListener("loadedmetadata", restore);
    el.src = withMediaToken(src, tokenRef.current);
  }, [rotation, engine, src, tokenRef]);

  // ------------------------------------------------------- media element state ---

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    const onTime = (): void => {
      if (!scrubbing) setCurrentTime(el.currentTime);
    };
    const onDuration = (): void => {
      if (Number.isFinite(el.duration) && el.duration > 0)
        setDuration(el.duration);
    };
    const onPlay = (): void => setPlaying(true);
    const onPause = (): void => setPlaying(false);
    const onRate = (): void => setRate(el.playbackRate);
    const onVolume = (): void => setMuted(el.muted);
    const onWaiting = (): void => setWaiting(true);
    const onPlaying = (): void => setWaiting(false);

    el.addEventListener("timeupdate", onTime);
    el.addEventListener("durationchange", onDuration);
    el.addEventListener("loadedmetadata", onDuration);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ratechange", onRate);
    el.addEventListener("volumechange", onVolume);
    el.addEventListener("waiting", onWaiting);
    el.addEventListener("playing", onPlaying);
    el.addEventListener("canplay", onPlaying);

    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("durationchange", onDuration);
      el.removeEventListener("loadedmetadata", onDuration);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ratechange", onRate);
      el.removeEventListener("volumechange", onVolume);
      el.removeEventListener("waiting", onWaiting);
      el.removeEventListener("playing", onPlaying);
      el.removeEventListener("canplay", onPlaying);
    };
  }, [scrubbing]);

  // Captions start hidden; the learner turns them on and the track follows.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const track = el.textTracks[0];
    if (!track) return;
    track.mode = captionsOn ? "showing" : "hidden";
  }, [captionsOn, video.captionsUrl]);

  // --------------------------------------------------------------- controls ---

  const seek = useCallback((to: number) => {
    const el = videoRef.current;
    if (!el) return;
    const max =
      Number.isFinite(el.duration) && el.duration > 0 ? el.duration : to;
    const next = Math.max(0, Math.min(to, max));
    // Any deliberate move of the playhead retires the automatic one.
    seededRef.current = true;
    el.currentTime = next;
    setCurrentTime(next);
    setResumeOffer(false);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      seekTo: (sec: number) => {
        seek(sec);
        const el = videoRef.current;
        if (el?.paused) void el.play().catch(() => undefined);
        shellRef.current?.scrollIntoView({ block: "nearest" });
      },
      pause: () => {
        videoRef.current?.pause();
      },
    }),
    [seek],
  );

  const togglePlay = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    // Pressing play instead of answering the resume card means "from here".
    seededRef.current = true;
    setResumeOffer(false);
    if (el.paused) void el.play().catch(() => undefined);
    else el.pause();
  }, []);

  const nudge = useCallback(
    (by: number) => {
      const el = videoRef.current;
      if (!el) return;
      seek(el.currentTime + by);
    },
    [seek],
  );

  const toggleMute = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
  }, []);

  const setSpeed = useCallback((value: number) => {
    const el = videoRef.current;
    if (!el) return;
    el.playbackRate = value;
  }, []);

  const chooseLevel = useCallback((value: number) => {
    setLevelChoice(value);
    const hls = hlsRef.current;
    if (hls) hls.currentLevel = value;
  }, []);

  const toggleFullscreen = useCallback(() => {
    const shell = shellRef.current;
    if (!shell) return;
    if (document.fullscreenElement)
      void document.exitFullscreen().catch(() => undefined);
    else void shell.requestFullscreen().catch(() => undefined);
  }, []);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      const typing =
        target instanceof HTMLInputElement && target.type !== "range"
          ? true
          : target instanceof HTMLTextAreaElement ||
            target?.isContentEditable === true;
      if (typing) return;

      switch (event.key) {
        case " ":
        case "k":
          event.preventDefault();
          togglePlay();
          break;
        case "ArrowLeft":
          event.preventDefault();
          nudge(-5);
          break;
        case "ArrowRight":
          event.preventDefault();
          nudge(5);
          break;
        case "j":
          event.preventDefault();
          nudge(-10);
          break;
        case "l":
          event.preventDefault();
          nudge(10);
          break;
        case "f":
          event.preventDefault();
          toggleFullscreen();
          break;
        case "m":
          event.preventDefault();
          toggleMute();
          break;
        case "c":
          if (video.captionsUrl) {
            event.preventDefault();
            setCaptionsOn((on) => !on);
          }
          break;
        default:
          break;
      }
    },
    [togglePlay, nudge, toggleFullscreen, toggleMute, video.captionsUrl],
  );

  const chapters = useMemo(
    () => [...video.chapters].sort((a, b) => a.startSec - b.startSec),
    [video.chapters],
  );

  const activeChapter = useMemo(() => {
    let found: VideoChapter | null = null;
    for (const chapter of chapters) {
      if (chapter.startSec <= currentTime + 0.25) found = chapter;
      else break;
    }
    return found;
  }, [chapters, currentTime]);

  // ------------------------------------------------------------- not ready ---

  if (!ready) {
    return <ProcessingPanel status={video.status} title={title} />;
  }

  const posterUrl = video.posterUrl ?? undefined;
  const scrubMax = duration > 0 ? duration : Math.max(currentTime, 1);

  return (
    <div className="flex flex-col gap-3">
      <div
        ref={shellRef}
        className="video-shell bg-surface-3 rounded-card overflow-hidden border border-line relative"
        onKeyDown={onKeyDown}
        role="region"
        aria-label={`Video player: ${title}`}
      >
        <div className="relative bg-surface-3">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption -- the caption
              track below is conditional on the author having published one. */}
          <video
            ref={videoRef}
            className="w-full aspect-video block bg-surface-3"
            poster={posterUrl}
            playsInline
            preload="metadata"
            crossOrigin="anonymous"
            tabIndex={0}
            aria-label={`${title} — video`}
            onClick={togglePlay}
          >
            {video.captionsUrl ? (
              <track
                kind="captions"
                src={video.captionsUrl}
                srcLang="en"
                label="English captions"
              />
            ) : null}
          </video>

          {engine === "unsupported" ? (
            <div className="absolute inset-0 grid place-items-center bg-surface-2 p-6 text-center">
              <p className="text-sm text-ink max-w-sm leading-relaxed">
                This browser cannot play the lecture. Streaming video here is
                adaptive HLS, which this browser neither supports natively nor
                can emulate.
              </p>
            </div>
          ) : null}

          {waiting && engine !== "unsupported" ? (
            <div
              className="absolute inset-0 grid place-items-center pointer-events-none"
              role="status"
              aria-label="Buffering"
            >
              <Loader2
                size={32}
                className="animate-spin text-ink"
                aria-hidden
              />
            </div>
          ) : null}

          {resumeOffer ? (
            <div className="absolute inset-x-3 bottom-3 sm:inset-x-auto sm:left-4 sm:max-w-sm bg-surface border border-line-strong rounded-control shadow-pop p-3">
              <p className="text-sm text-ink">
                You stopped at <strong>{formatDuration(resumeFromSec)}</strong>{" "}
                last time.
              </p>
              <div className="flex gap-2 mt-2.5">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => {
                    seek(resumeFromSec);
                    void videoRef.current?.play().catch(() => undefined);
                  }}
                >
                  Resume from {formatDuration(resumeFromSec)}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    seededRef.current = true;
                    setResumeOffer(false);
                  }}
                >
                  Start over
                </Button>
              </div>
            </div>
          ) : null}
        </div>

        <ControlBar
          playing={playing}
          muted={muted}
          rate={rate}
          currentTime={currentTime}
          duration={scrubMax}
          chapters={chapters}
          captionsAvailable={!!video.captionsUrl}
          captionsOn={captionsOn}
          levels={levels}
          levelChoice={levelChoice}
          activeLevel={activeLevel}
          engine={engine}
          onTogglePlay={togglePlay}
          onToggleMute={toggleMute}
          onSeek={(value) => {
            setScrubbing(true);
            setCurrentTime(value);
          }}
          onSeekCommit={(value) => {
            setScrubbing(false);
            seek(value);
          }}
          onSetSpeed={setSpeed}
          onChooseLevel={chooseLevel}
          onToggleCaptions={() => setCaptionsOn((on) => !on)}
          onFullscreen={toggleFullscreen}
        />
      </div>

      {engine === "unsupported" ? (
        <Alert tone="warn" title="This browser cannot play the lecture">
          The video is delivered as adaptive HLS, which this browser does not
          support and cannot emulate. Chrome, Edge, Firefox and Safari all play
          it — switching browsers is the fix.
        </Alert>
      ) : null}
      {fatalError ? (
        <Alert tone="danger" title="Playback stopped">
          {fatalError}
        </Alert>
      ) : null}
      {tokenError ? (
        <Alert tone="warn" title="Playback pass expiring">
          {tokenError}
        </Alert>
      ) : null}

      <ShortcutHint />

      {chapters.length > 0 ? (
        <ChapterList
          chapters={chapters}
          activeId={activeChapter?.id ?? null}
          onJump={seek}
        />
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------ controls ---

function ControlBar({
  playing,
  muted,
  rate,
  currentTime,
  duration,
  chapters,
  captionsAvailable,
  captionsOn,
  levels,
  levelChoice,
  activeLevel,
  engine,
  onTogglePlay,
  onToggleMute,
  onSeek,
  onSeekCommit,
  onSetSpeed,
  onChooseLevel,
  onToggleCaptions,
  onFullscreen,
}: {
  playing: boolean;
  muted: boolean;
  rate: number;
  currentTime: number;
  duration: number;
  chapters: VideoChapter[];
  captionsAvailable: boolean;
  captionsOn: boolean;
  levels: Level[];
  levelChoice: number;
  activeLevel: number;
  engine: Engine;
  onTogglePlay: () => void;
  onToggleMute: () => void;
  onSeek: (value: number) => void;
  onSeekCommit: (value: number) => void;
  onSetSpeed: (value: number) => void;
  onChooseLevel: (value: number) => void;
  onToggleCaptions: () => void;
  onFullscreen: () => void;
}) {
  const timeLabel = `${formatDuration(currentTime)} of ${formatDuration(duration)}`;

  return (
    <div className="bg-surface border-t border-line px-2 sm:px-3 py-2">
      <div className="relative py-2">
        <input
          type="range"
          min={0}
          max={duration}
          step={0.5}
          value={Math.min(currentTime, duration)}
          aria-label="Seek through the video"
          aria-valuetext={timeLabel}
          onChange={(e) => onSeek(Number(e.target.value))}
          onPointerUp={(e) =>
            onSeekCommit(Number((e.target as HTMLInputElement).value))
          }
          onMouseUp={(e) =>
            onSeekCommit(Number((e.target as HTMLInputElement).value))
          }
          onTouchEnd={(e) =>
            onSeekCommit(Number((e.target as HTMLInputElement).value))
          }
          onKeyUp={(e) =>
            onSeekCommit(Number((e.target as HTMLInputElement).value))
          }
          onBlur={(e) => onSeekCommit(Number(e.target.value))}
          className="w-full accent-brand cursor-pointer relative z-10"
        />
        {duration > 0 && chapters.length > 0 ? (
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1.5 pointer-events-none">
            {chapters.map((chapter) => (
              <span
                key={chapter.id}
                aria-hidden
                title={chapter.title}
                className="absolute top-0 h-1.5 w-0.5 bg-ink-subtle/70 rounded-full"
                style={{
                  left: `${Math.min(100, (chapter.startSec / duration) * 100)}%`,
                }}
              />
            ))}
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={onTogglePlay}
          aria-label={playing ? "Pause" : "Play"}
          className="inline-flex items-center justify-center h-9 w-9 rounded-control text-ink hover:bg-surface-2"
        >
          {playing ? (
            <Pause size={18} aria-hidden />
          ) : (
            <Play size={18} aria-hidden />
          )}
        </button>

        <button
          type="button"
          onClick={onToggleMute}
          aria-label={muted ? "Unmute" : "Mute"}
          aria-pressed={muted}
          className="inline-flex items-center justify-center h-9 w-9 rounded-control text-ink hover:bg-surface-2"
        >
          {muted ? (
            <VolumeX size={18} aria-hidden />
          ) : (
            <Volume2 size={18} aria-hidden />
          )}
        </button>

        <span className="text-xs tabular-nums text-ink-muted px-1.5 min-w-28">
          <span className="sr-only">Position: </span>
          {timeLabel}
        </span>

        <span className="flex-1" />

        <PopMenu
          label={`Playback speed, currently ${rate}×`}
          trigger={
            <>
              <Gauge size={15} aria-hidden />
              <span className="tabular-nums">{rate}×</span>
            </>
          }
        >
          {SPEEDS.map((speed) => (
            <MenuItem
              key={speed}
              selected={rate === speed}
              onSelect={() => onSetSpeed(speed)}
              label={`${speed}× speed`}
            >
              {speed}×{speed === 1 ? " (normal)" : ""}
            </MenuItem>
          ))}
        </PopMenu>

        {engine === "hls" && levels.length > 1 ? (
          <PopMenu
            label="Video quality"
            trigger={
              <>
                <SlidersHorizontal size={15} aria-hidden />
                <span>
                  {levelChoice === -1
                    ? `Auto${activeLevel >= 0 && levels[activeLevel] ? ` · ${levels[activeLevel]?.height}p` : ""}`
                    : `${levels[levelChoice]?.height ?? "—"}p`}
                </span>
              </>
            }
          >
            <MenuItem
              selected={levelChoice === -1}
              onSelect={() => onChooseLevel(-1)}
              label="Automatic quality, matched to your connection"
            >
              Auto
            </MenuItem>
            {levels.map((level, index) => (
              <MenuItem
                key={`${level.height}-${level.bitrate}-${index}`}
                selected={levelChoice === index}
                onSelect={() => onChooseLevel(index)}
                label={`${level.height}p quality`}
              >
                {level.height}p
              </MenuItem>
            ))}
          </PopMenu>
        ) : null}

        <button
          type="button"
          onClick={onToggleCaptions}
          disabled={!captionsAvailable}
          aria-pressed={captionsOn}
          aria-label={
            captionsAvailable
              ? captionsOn
                ? "Turn captions off"
                : "Turn captions on"
              : "Captions are not available for this video"
          }
          title={
            captionsAvailable
              ? "Captions"
              : "This video has no caption track yet"
          }
          className={clsx(
            "inline-flex items-center justify-center h-9 w-9 rounded-control hover:bg-surface-2",
            captionsOn ? "text-brand-text" : "text-ink",
            !captionsAvailable &&
              "opacity-40 cursor-not-allowed hover:bg-transparent",
          )}
        >
          {captionsOn ? (
            <Captions size={18} aria-hidden />
          ) : (
            <CaptionsOff size={18} aria-hidden />
          )}
        </button>

        <button
          type="button"
          onClick={onFullscreen}
          aria-label="Full screen"
          className="inline-flex items-center justify-center h-9 w-9 rounded-control text-ink hover:bg-surface-2"
        >
          <Maximize size={18} aria-hidden />
        </button>
      </div>
    </div>
  );
}

function PopMenu({
  label,
  trigger,
  children,
}: {
  label: string;
  trigger: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocDown = (event: MouseEvent): void => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onEsc = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-control text-xs font-medium text-ink hover:bg-surface-2"
      >
        {trigger}
      </button>
      {open ? (
        <div
          role="menu"
          aria-label={label}
          className="absolute right-0 bottom-full mb-1 z-20 min-w-36 rounded-control border border-line bg-surface shadow-pop p-1"
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

function MenuItem({
  selected,
  onSelect,
  label,
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      aria-label={label}
      onClick={onSelect}
      className={clsx(
        "w-full text-left text-sm px-3 py-1.5 rounded-control hover:bg-surface-2 flex items-center justify-between gap-3",
        selected ? "text-ink font-medium" : "text-ink-muted",
      )}
    >
      {children}
      {selected ? <Check size={14} aria-hidden className="text-brand-text shrink-0" /> : null}
    </button>
  );
}

function ShortcutHint() {
  const items: [string, string][] = [
    ["Space", "play / pause"],
    ["← →", "5 seconds"],
    ["J L", "10 seconds"],
    ["M", "mute"],
    ["C", "captions"],
    ["F", "full screen"],
  ];
  return (
    <p className="text-xs text-ink-subtle flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="font-medium text-ink-muted">
        Keyboard, while the player has focus:
      </span>
      {items.map(([key, what]) => (
        <span key={key} className="inline-flex items-center gap-1">
          <kbd className="border border-line rounded px-1.5 py-0.5 bg-surface-2 font-mono text-[0.7rem]">
            {key}
          </kbd>
          {what}
        </span>
      ))}
    </p>
  );
}

function ChapterList({
  chapters,
  activeId,
  onJump,
}: {
  chapters: VideoChapter[];
  activeId: string | null;
  onJump: (sec: number) => void;
}) {
  return (
    <nav aria-label="Chapters in this video">
      <h3 className="text-sm font-semibold text-ink mb-2">Chapters</h3>
      <ol className="flex flex-col gap-0.5">
        {chapters.map((chapter, index) => (
          <li key={chapter.id}>
            <button
              type="button"
              onClick={() => onJump(chapter.startSec)}
              aria-current={activeId === chapter.id ? "true" : undefined}
              className={clsx(
                "w-full flex items-baseline gap-3 text-left text-sm border-l-2 pl-2.5 pr-2 py-1.5 rounded-r-control",
                "transition-colors duration-150 ease-out hover:bg-surface-2",
                activeId === chapter.id
                  ? "border-brand bg-surface-2 text-ink font-semibold"
                  : "border-line text-ink-muted hover:border-line-strong",
              )}
            >
              <span className="font-mono tabular-nums text-xs text-ink-subtle w-12 shrink-0">
                {formatDuration(chapter.startSec)}
              </span>
              <span className="min-w-0">
                <span className="sr-only">Chapter {index + 1}: </span>
                {chapter.title}
              </span>
            </button>
          </li>
        ))}
      </ol>
    </nav>
  );
}

// ----------------------------------------------------------------- pipeline ---

const STAGE_COPY: Record<string, { label: string; detail: string }> = {
  awaiting_upload: {
    label: "Waiting for the video file",
    detail: "Nothing has been uploaded to this session yet.",
  },
  uploading: {
    label: "Upload in progress",
    detail: "The source file is still being transferred.",
  },
  uploaded: {
    label: "Queued for processing",
    detail: "The upload finished and the encoder has not picked it up yet.",
  },
  transcoding: {
    label: "Transcoding",
    detail:
      "The encoder is producing the streaming renditions. This usually takes a few minutes per hour of footage.",
  },
  failed: {
    label: "Processing failed",
    detail:
      "The encoder could not finish this file. The session author can requeue it from the console.",
  },
};

function ProcessingPanel({
  status,
  title,
}: {
  status: VideoPlayback["status"];
  title: string;
}) {
  const copy = STAGE_COPY[status] ?? {
    label: "Not ready yet",
    detail: "This video is not available for playback yet.",
  };

  return (
    <Card className="p-6">
      <div className="flex items-start gap-4">
        <span
          aria-hidden
          className={clsx(
            "grid place-items-center h-10 w-10 shrink-0 rounded-control border",
            status === "failed"
              ? "bg-danger-wash border-danger/30 text-danger"
              : "bg-surface-2 border-line text-ink-muted",
          )}
        >
          {status === "failed" ? (
            "!"
          ) : (
            <Loader2 size={18} className="animate-spin" />
          )}
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-ink">{copy.label}</h3>
            <Badge tone={status === "failed" ? "danger" : "warn"}>
              {status.replace(/_/g, " ")}
            </Badge>
          </div>
          <p className="text-sm text-ink-muted mt-1.5 leading-relaxed">
            {copy.detail}
          </p>
          <p className="text-xs text-ink-subtle mt-2">
            You are seeing this because you can author “{title}”. Learners see
            the rest of the session without a player until the video is ready.
          </p>
        </div>
      </div>
    </Card>
  );
}
